import { mapState } from 'vuex';
import { apiCallers } from '@/api/apiRequest';
import { formatMoney } from '@/modules/utils';
import moment from 'moment';

import ThemeHandler from '@/mixins/themeHandler.js';
import NoaChart from './chart';
import NRadio from '@/components/common/n-radio';
import VkbDatePicker from '@/components/common/vkb-date-picker';
import VkbDataTable from '@/components/common/vkb-data-table';

export default {
  components: {
    NoaChart,
    NRadio,
    VkbDatePicker,
    VkbDataTable,
  },

  mixins: [
    ThemeHandler
  ],

  data: () => ({
    date_from: moment().subtract(1, 'months').format('DD.MM.YYYY'),
    date_to: moment().format('DD.MM.YYYY'),
    date_range: 1,
    chart_data: [],
    table_data: [],
    summary_data: {},
    min_date: '03.10.1975',
    max_date: moment().format('DD.MM.YYYY'),
    showTable: false,

    dateOffset: [
      {
        amount: 1,
        unit: 'months'
      },
      {
        amount: 6,
        unit: 'months'
      },
      {
        amount: 1,
        unit: 'years'
      },
      {
        amount: 3,
        unit: 'years'
      },
      {
        amount: 5,
        unit: 'years'
      },
    ],

    radioOptions: [
      {
        text: 'Ja',
        value: true
      },
      {
        text: 'Nein',
        value: false
      }
    ],

    headers: [
      {
        text: 'Datum',
        value: 'date',
        align: 'center',
        slot_header: true,
        slot_data: true
      },
      {
        text: 'Kurswert',
        value: 'value',
        align: 'center',
        slot_header: true,
        slot_data: true
      }
    ],

    dateRanges: {
      1: '1Monat',
      2: '6Monat',
      3: '1Jahr',
      4: '3Jahr',
      5: '5Jahr',
      6: 'Auflage',
    }
  }),

  computed: {
    ...mapState([
      'appConfigs'
    ]),

    apiRequest () {
      return apiCallers[this.config.app];
    },

    config () {
      return this.appConfigs[this.$options.configIndex] || {};
    },

    today () {
      return moment().format('YYYY-MM-DD');
    },

    rangeConfig () {
      return this.config.RangeConfig || {};
    },

    chartMin () {
      const rangeId = this.dateRanges[this.date_range];
      if (!rangeId || !this.rangeConfig[rangeId] || this.rangeConfig[rangeId].Min === undefined) {
        return null;
      }
      return this.rangeConfig[rangeId].Min;
    },

    chartMax () {
      const rangeId = this.dateRanges[this.date_range];
      if (!rangeId || !this.rangeConfig[rangeId] || this.rangeConfig[rangeId].Max === undefined) {
        return null;
      }
      return this.rangeConfig[rangeId].Max;
    }
  },

  mounted() {
    this.chart_date_from = this.date_from;
    this.chart_date_to = this.date_to;
    this.loadResult();
  },

  methods: {
    closeOtherPicker(name) {
      const date_picker = this.$refs[name];

      if (date_picker) {
        date_picker.onCloseDatePicker();
      }
    },

    formatValue(value, decimals = 2) {
      if (!value) {
        return '0,00';
      }

      return formatMoney(value, decimals);
    },

    formatDate(value, old_format = '', new_format = 'DD.MM.YYYY') {
      let date = old_format
        ? moment(value, old_format)
        : moment(value);

      if (!date.isValid()) {
        return '';
      }

      return date.format(new_format);
    },

    generateChartData(values) {
      let date_from = this.chart_date_from
        ? moment(this.chart_date_from, 'DD.MM.YYYY')
        : null;

      let date_to = moment(this.chart_date_to, 'DD.MM.YYYY');

      if (!date_from) {
        date_from = this.getDateFrom(date_to);
      }

      let day_diff = date_to.diff(date_from, 'days') + 1;

      if (day_diff < 0) {
        return this.chart_data = [];
      }

      let chart_values = {};

      values
        .map(d => {
          const date = d.value_date.substring(0, 10);
          chart_values[moment(date).format('x')] = d.value;
        });

      this.chart_data = new Array(day_diff)
        .fill(0)
        .map((_, i) => {
          let chart_date = date_from.clone().add(i, 'days').format('x');
          let chart_value = chart_values[chart_date];

          return [
            parseInt(chart_date),
            chart_value || null
          ];
        });
    },

    getDateFrom (toDate = '') {
      if (this.date_range === 6) {
        return moment('1975-10-03');
      } else {
        const offsetId = this.date_range - 1;
        return moment(toDate).subtract(this.dateOffset[offsetId].amount, this.dateOffset[offsetId].unit);
      }
    },

    loadResult(isInit = true) {
      if (!this.apiRequest) {
        return;
      }

      this.$v.$touch();
      if(this.$v.$invalid) {
        return ;
      }

      let _params = {
        index_name: this.config.index_name || 'VK_MSCI_World',
        date_range: this.date_range,
        date_from: this.date_from
          ? moment(this.date_from, 'DD.MM.YYYY').format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
          : null,
        date_to: this.date_to
          ? moment(this.date_to, 'DD.MM.YYYY').format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
          : null
      };

      this.apiRequest
        .get(this.config.paths.query, {
          params: _params
        })
        .then(res => {
          this.summary_data = res.data;
          this.summary_data.details = this.summary_data.details || [];

          const values = this.summary_data.details
            .sort((a, b) => a.value_date > b.value_date ? 1 : -1);

          if (values.length) {
            this.chart_date_from = moment(values[0].value_date).format('DD.MM.YYYY');
            this.chart_date_to = moment(values[values.length - 1].value_date).format('DD.MM.YYYY');
          }

          // set min, max date
          this.$nextTick(() => {
            if (isInit) {
              this.min_date = moment(this.summary_data.auflagedatum).format('DD.MM.YYYY');
              this.max_date = moment(this.summary_data.letzer_wert_datum).format('DD.MM.YYYY');

              if (this.date_range === 6) {
                this.date_from = this.min_date;
                this.date_to = moment(this.summary_data.stand).format('DD.MM.YYYY');
              } else if (values.length) {
                this.date_from = moment(values[0].value_date).format('DD.MM.YYYY');
                this.date_to = moment(values[values.length - 1].value_date).format('DD.MM.YYYY');
              } else {
                this.date_from = '';
                this.date_to = '';
              }
            }
          });

          this.table_data = values
            .map(d => ({
              date: d.value_date,
              value: d.value
            }));

          if (this.date_range < 6) {
            return this.generateChartData(values);
          }

          this.chart_data = values.map(d => ([
            parseInt(moment(d.value_date, 'YYYY-MM-DD[T]00:00:00Z').format('x')),
            d.value
          ]));
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.$v.$reset();
        });
    },

    onSelectDateRange(value) {
      this.date_range = value;
      this.date_to = moment().format('DD.MM.YYYY');
      this.date_from = this.getDateFrom(moment()).format('DD.MM.YYYY');

      this.chart_date_from = this.date_from;
      this.chart_date_to = this.date_to;

      this.min_date = this.date_from;
      this.max_date = this.date_to;

      this.loadResult();
    }
  },

  validations: {
    date_from: {
      min(value) {
        if (!value) {
          return true;
        }

        let date = moment(value, 'DD.MM.YYYY');

        if (!date.isValid()) {
          return true;
        }

        let min_date = moment(this.min_date, 'DD.MM.YYYY');

        return date >= min_date;
      },

      max(value) {
        if (!value) {
          return true;
        }

        let date = moment(value, 'DD.MM.YYYY');

        if (!date.isValid()) {
          return true;
        }

        let max_date = moment(this.max_date, 'DD.MM.YYYY');

        return date <= max_date;
      }
    },

    date_to: {
      valid(value) {
        if (!value) {
          return false;
        }

        let date = moment(value, 'DD.MM.YYYY');

        return date.isValid();
      },

      min(value) {
        let date = moment(value, 'DD.MM.YYYY');
        let min_date = moment(this.min_date, 'DD.MM.YYYY');

        return date >= min_date;
      },

      max(value) {
        let date = moment(value, 'DD.MM.YYYY');
        let max_date = moment(this.max_date, 'DD.MM.YYYY');

        return date <= max_date;
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/apps/index_app/logic.js